import { useEffect, useState } from 'react';
import { summaryWaitMessage } from '../utils/summaryProgress';

/**
 * 待ちがこの時間を超えたら、案内を切り替えてスピナーを止める（課題 #102）。
 *
 * 実測で 8 分・十数分・2 時間超という報告があり、「最大 1 分程度」という案内と
 * 大きく食い違っていた。回り続けるスピナーは「壊れているのか待てばよいのか」を
 * 判断できなくする。待てば表示されることを伝えて、画面を離れられるようにする。
 *
 * 10 分にしているのは、ここで待っているのが**解析ではなくレポート生成**だと
 * BE から共有を受けたため（2026-09-23）。解析自体は 1〜2 秒で終わるが、
 * レポート生成のワーカーが 5 分間隔で動く。5 分だと正常な待ち時間の境目で
 * 「時間がかかっています」と出てしまい、かえって不安にさせる。
 */
const ANALYZING_LONG_MS = 10 * 60 * 1000;

/**
 * まとめの反映を追いかける間隔。生成は 5 分間隔なので、細かく叩いても早くならない。
 * 1 回のポーリングで複数の API を呼ぶため、間隔を詰めすぎない。
 */
const POLL_INTERVAL_MS = 15_000;

/**
 * まとめができるまで追いかけ、待っている間の文言を返す（課題 #42 / #102 / #174）。
 *
 * 以前は 3 秒間隔・最大 60 秒で止めていたが、まとめの生成は最大 5 分かかるため、
 * 60 秒で止まると「完了すると表示されます」と出したまま画面が更新されなかった。
 * 案内の切り替え（10 分）を少し超えるまで追う。
 *
 * 目安の時刻は待ち始めに読んだものを持ち続ける。BE は読むたびに次の時刻を返すので、
 * 読み直すと、作り始めた直後に「あと約 1 分」が「あと約 5 分」へ戻ってしまう。
 */
export function useSummaryWait(
  waiting: boolean,
  nextGenerationAt: string | null | undefined,
  reload: () => void
) {
  const [tooLong, setTooLong] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!waiting) {
      setTarget(null);
      return;
    }
    if (nextGenerationAt) setTarget((prev) => prev ?? nextGenerationAt);
  }, [waiting, nextGenerationAt]);

  useEffect(() => {
    if (!waiting) {
      setTooLong(false);
      return;
    }
    const startedAt = Date.now();
    setNow(startedAt);
    const clock = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current - startedAt >= ANALYZING_LONG_MS) setTooLong(true);
    }, 10_000);
    let attempts = 0;
    const maxAttempts = Math.ceil((ANALYZING_LONG_MS + 60_000) / POLL_INTERVAL_MS);
    const poll = setInterval(() => {
      attempts += 1;
      if (attempts >= maxAttempts) {
        clearInterval(poll);
        return;
      }
      reload();
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(clock);
      clearInterval(poll);
    };
  }, [waiting, reload]);

  return { tooLong, message: summaryWaitMessage(target, now, tooLong) };
}
